import { isDefined } from "@clipboard-health/util-ts";
import { SpecialQualifications } from "@src/appV2/Qualifications/types";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";
import { useFilterBackgroundCheckRequirements } from "@src/appV2/redesign/BackgroundChecks/useFilterBackgroundCheckRequirements";
import { type WorkerRequirement } from "@src/appV2/redesign/Documents/api/types";
import { useGetRequirementsForWorkplaces } from "@src/appV2/redesign/Documents/api/wrappers/useGetRequirementsForWorkplaces";
import { getDocumentCheckQualificationFromMultipleQualifications } from "@src/appV2/redesign/Documents/helpers";
import { filterBlockingRequirementsForShift } from "@src/appV2/redesign/Documents/utils/filterBlockingRequirementsForShift";
import { useShiftDiscoveryUserFiltersContext } from "@src/appV2/redesign/ShiftDiscovery/Filters/useUserFiltersContext";

import { NON_REJECTED_WORKER_REQUIREMENT_STATUSES } from "../../BackgroundChecks/constants";
import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { getBlockingRequirementsContext } from "../Bookability/Documents/getBlockingRequirementsContext";
import type { OpenShiftListItem, OpenShiftWorkplace } from "./types";

interface OpenShiftsMissingRequirementsProps {
  openShifts: OpenShiftListItem[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
}

interface GetShiftsBlockingRequirementsMapProps {
  openShifts: OpenShiftListItem[];
  workerQualifications: string[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
  credentialRequirementConfiguration: Set<string>;
  workplaceRequirements?: WorkerRequirement[];
  workerCustomToBaseQualificationNameMap?: Map<string, string>;
}

function getShiftsBlockingRequirementsMap(props: GetShiftsBlockingRequirementsMapProps) {
  const {
    openShifts,
    workerQualifications,
    workplacesMap,
    credentialRequirementConfiguration,
    workplaceRequirements,
    workerCustomToBaseQualificationNameMap,
  } = props;
  return new Map(
    openShifts.map((shift) => {
      if (shift.type === "in-app-ad") {
        // This should eventually be any injection not just in-app-ads
        // no blocking requirements for injected items
        return [shift.appId, null];
      }

      if (shift.type === "fortune-cookie") {
        // No blocking requirements for fortune cookie injections
        return [shift.type, null];
      }

      const qualificationForDocumentCheck = getDocumentCheckQualificationFromMultipleQualifications(
        {
          shiftQualificationRequirement: shift.attributes.qualification,
          selectedWorkerQualifications: workerQualifications,
        }
      );
      const baseQualificationName = workerCustomToBaseQualificationNameMap?.get(
        qualificationForDocumentCheck ?? shift.attributes.qualification
      );

      const qualifications = [
        qualificationForDocumentCheck ?? shift.attributes.qualification,
        baseQualificationName,
      ].filter(isDefined);

      const workplaceId = shift.relationships.workplace.data.id;
      const workplace = workplacesMap.get(workplaceId);
      const blockingRequirementsForShift = filterBlockingRequirementsForShift({
        workplace: {
          id: workplaceId,
          workplaceType: workplace?.attributes.type,
          msa: workplace?.attributes.location.msa,
          state: workplace?.attributes.location.state,
          region: workplace?.attributes.location.region,
        },
        qualifications,
        workplaceRequirements,
        expiryDate: shift.attributes.end,
      });

      const blockingRequirementsContext = getBlockingRequirementsContext(
        blockingRequirementsForShift ?? [],
        credentialRequirementConfiguration,
        shift.attributes.end
      );

      return [shift.id, blockingRequirementsContext];
    })
  );
}

/**
 * Aggregates the blocking requirements context for given open shifts
 * @returns map of shift id to blocking requirements context
 */
export function useOpenShiftsBlockingRequirementsContext(
  props: OpenShiftsMissingRequirementsProps
) {
  const { openShifts, workplacesMap } = props;

  const { licenses: selectedLicensesFilter, availableLicenses } =
    useShiftDiscoveryUserFiltersContext();

  const selectedWorkerQualifications =
    isDefined(selectedLicensesFilter) && selectedLicensesFilter.length > 0
      ? selectedLicensesFilter
      : availableLicenses;

  // CAREGIVER shifts are shown to workers with the CNA qualification.
  const workerQualificationsForDocumentCheck = selectedWorkerQualifications.includes(
    SpecialQualifications.CNA
  )
    ? [...selectedWorkerQualifications, SpecialQualifications.CAREGIVER]
    : selectedWorkerQualifications;

  const workerCustomToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();
  const baseQualificationNames = workerQualificationsForDocumentCheck.map(
    (qualification) => workerCustomToBaseQualificationNameMap.get(qualification) ?? qualification
  );
  const qualifications = [
    ...new Set([...workerQualificationsForDocumentCheck, ...baseQualificationNames]),
  ];

  const { data: workplaceRequirements } = useGetRequirementsForWorkplaces(
    {
      workplaceIds: Array.from(workplacesMap.values(), (workplace) => workplace.id),
      qualifications,
    },
    {
      enabled: openShifts.length > 0,
    }
  );

  const { filterOutBackgroundCheckOnlyIfNotAutoUnassigned } =
    useFilterBackgroundCheckRequirements();

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  return getShiftsBlockingRequirementsMap({
    openShifts,
    workerQualifications: workerQualificationsForDocumentCheck,
    workplacesMap,
    // ACT-3210: When run-background-check-on-booking is enabled, we hide other than REJECTED UI for Checkr background checks.
    // For non-rejected statuses, such as MISSING, APPROVED, EXPIRING, IN_REVIEW, EXPIRED, we want to hide the background check
    // details since the worker cannot take an action on it (as it runs automatically on booking). However, if the worker was auto-unassigned, we don't want to hide the background check details and relay information.
    workplaceRequirements: isDefined(workplaceRequirements)
      ? filterOutBackgroundCheckOnlyIfNotAutoUnassigned(workplaceRequirements, {
          status: NON_REJECTED_WORKER_REQUIREMENT_STATUSES,
        })
      : workplaceRequirements,
    workerCustomToBaseQualificationNameMap,
    credentialRequirementConfiguration,
  });
}
